import { useEffect } from "react";
import Reveal from "@/components/Reveal";

const SECTIONS = [
  {
    label: "— What this is",
    body: 'The QPI Labs Photonic Inference Simulator ("the Simulator") is a research and evaluation tool for exploring photonic neural network compute. Access is provided by QPI Laboratories, Inc. ("QPI Labs", "we", "us") and governed by these Terms of Use. By accessing the Simulator, you agree to these terms.',
  },
  {
    label: "— Access",
    body: "Access to the Simulator is granted by QPI Labs on a provisional, non-exclusive basis following a manual review of your access request. We reserve the right to grant, decline, suspend, or revoke access at any time without prior notice and without obligation to provide a reason.",
  },
  {
    label: "— Permitted use",
    body: "The Simulator is provided for research, education, and evaluation purposes only. You may use simulation outputs — accuracy figures, throughput numbers, Pareto curves, and exported plots — in academic papers, presentations, and internal engineering evaluations, provided you cite QPI Laboratories as the source. Commercial use of simulation outputs to validate or market a photonic hardware product requires a separate written agreement with QPI Labs.",
  },
  {
    label: "— Restrictions",
    body: "You may not reverse-engineer, decompile, copy, scrape, or redistribute the Simulator or any part of its codebase, physics models, or user interface. You may not use the Simulator to train or fine-tune a competing simulation product. You may not share your login credentials with others — access is provisioned per individual.",
  },
  {
    label: "— No warranty",
    body: 'The Simulator is provided "as is" without warranty of any kind. Simulation outputs are physics-informed estimates — they are not engineering guarantees. QPI Labs makes no representation that results from the Simulator will match the behaviour of any real photonic hardware implementation. All hardware design decisions remain the sole responsibility of the user.',
  },
  {
    label: "— Limitation of liability",
    body: "To the maximum extent permitted by California law, QPI Labs shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from your use of or reliance on the Simulator or its outputs, including but not limited to tapeout decisions, hardware procurement, or investment decisions.",
  },
  {
    label: "— Governing law & contact",
    body: "These Terms are governed by the laws of the State of California without regard to conflict of law principles. Any disputes shall be resolved in the courts of San Diego County, California. For questions about these Terms, contact us at info@qpilabs.ai.",
  },
];

function Body({ text }) {
  if (text.includes("info@qpilabs.ai")) {
    const [before, after] = text.split("info@qpilabs.ai");
    return (
      <p className="font-mono text-[13px] leading-relaxed text-zinc-400">
        {before}
        <a
          href="mailto:info@qpilabs.ai"
          data-testid="terms-contact-link"
          className="text-beam transition-colors hover:underline"
        >
          info@qpilabs.ai
        </a>
        {after}
      </p>
    );
  }
  return <p className="font-mono text-[13px] leading-relaxed text-zinc-400">{text}</p>;
}

export default function Terms() {
  useEffect(() => {
    document.title = "Terms of Use · QPI Labs";
  }, []);

  return (
    <main className="pt-16" data-testid="terms-page">
      <section className="border-b border-white/10 py-20 md:py-28">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow mb-6 text-laser">— Legal</p>
            <h1 className="max-w-4xl text-5xl font-extrabold tracking-tighter md:text-7xl">
              Terms of Use
            </h1>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              Effective October 2026 · QPI Laboratories, Inc. · California, United States
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto w-full max-w-[720px] px-6">
          {SECTIONS.map((section, i) => (
            <Reveal key={section.label}>
              <article
                className="border-t border-white/10 py-14 first:border-t-0 first:pt-0 md:py-16"
                data-testid={`terms-section-${i + 1}`}
              >
                <p className="eyebrow mb-5">{section.label}</p>
                <Body text={section.body} />
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
